import type { ReactNode } from 'react'

import { ChevronLeftIcon, CloseIcon } from '~/components/icons'
import { cn } from '~/lib/cn'

/**
 * The line a panel opens with: the way out of it — back to whatever it was reached from
 * inside the same dialog, or out of the dialog altogether where it was reached from nothing —
 * and its name.
 */
export function PanelHeader({
  title,
  subtitle,
  onBack,
  backLabel,
  close = false,
  danger = false,
  children,
}: {
  title: string
  /**
   * What the panel's subject is, said under its name — a template's schedule, and nothing
   * whose absence would leave the line looking short. A panel about a thing rather than about
   * a page carries one; the rest go without.
   */
  subtitle?: string | undefined
  onBack: () => void
  backLabel?: string
  /**
   * Whether the way out leaves the dialog rather than stepping back inside it. A panel opened
   * on its own has nothing behind it, and a chevron pointing at nothing reads as a way back to
   * something the reader has not seen.
   */
  close?: boolean
  /** A panel whose subject is losing something names itself in the danger ink. */
  danger?: boolean
  /** Whatever else the line carries, laid against its far end. */
  children?: ReactNode
}) {
  return (
    <header className="flex items-center gap-1 px-3 pt-3 pb-1">
      <button
        type="button"
        aria-label={backLabel ?? (close ? 'Close' : 'Back')}
        className="rounded-full p-1.5 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        onClick={onBack}
      >
        {close ? (
          <CloseIcon aria-hidden className="size-6" />
        ) : (
          <ChevronLeftIcon aria-hidden className="size-6" />
        )}
      </button>
      <div className="min-w-0 flex-1 px-1">
        <h2 className={cn('truncate text-lg font-medium', danger && 'text-danger')}>{title}</h2>
        {subtitle === undefined ? null : (
          <p className="truncate text-sm text-ink-muted">{subtitle}</p>
        )}
      </div>
      {children}
    </header>
  )
}
